import { useEffect, useMemo, useState } from 'react';

import { rgbToHex } from '../../../../helpers/rgbToHex';
import { ChunkedImageProperties } from '../../../../types/chunkedImage';
import { LengthRange } from '../../../../types/lengthRange';

export interface UseHoverLocationColourProps {
	positionY: number | undefined;
	colour: string | undefined;
}

interface OffscreenChunkCanvasContext {
	context: OffscreenCanvasRenderingContext2D | null;
	chunk: HoverChunkImageProperties;
}

interface HoverChunkImageProperties {
	properties: ChunkedImageProperties;
	x: number;
	width: number;
	scaleFactor: number;
}

export const useHoverLocationColour = (
	coordinates: [x: number, y: number] | undefined,
	chunks: ChunkedImageProperties[],
	lane: LengthRange,
	scale: number,
	shouldCalculate?: boolean,
): UseHoverLocationColourProps | undefined => {
	const [offscreenCanvas, setOffscreenCanvas] = useState<OffscreenChunkCanvasContext>();
	const [hoverChunk, setHoverChunk] = useState<ChunkedImageProperties>();
	const [hexColour, setHexColour] = useState<string>();
	const [positionY, setPositionY] = useState<number>();

	useEffect(() => {
		if (!shouldCalculate) setHexColour(undefined);
	}, [shouldCalculate]);

	useEffect(() => {
		if (!coordinates) {
			setPositionY(undefined);
			return;
		}
		const [internalX] = coordinates;
		const calculatedY = lane.depthFrom + internalX / scale;
		setPositionY(calculatedY);
	}, [coordinates, lane.depthFrom, scale]);

	useEffect(() => {
		setHoverChunk((prevHoverChunk) => {
			if (positionY === undefined) return undefined;

			if (
				prevHoverChunk &&
				positionY >= prevHoverChunk.range.depthFrom &&
				positionY <= prevHoverChunk.range.depthTo
			)
				return prevHoverChunk; // Existing chunk still under cursor

			return Object.values(chunks)
				.sort((a, b) => b.originalResPxPerM - a.originalResPxPerM)
				.find((c) => c.range && c.url && positionY >= c.range.depthFrom && positionY <= c.range.depthTo);
		});
	}, [chunks, positionY]);

	useEffect(() => {
		if (!hoverChunk) {
			setOffscreenCanvas(undefined);
			return;
		}

		if (!shouldCalculate) return;

		let shouldUpdate = true;

		// Render chunk image at max resolution offscreen so that pixel RGB values can be read
		// on mouse movements. The canvas is rebuilt only when the hovered chunk itself changes;
		// the cursor position is consumed by the sampling effect below, not here.
		const promise = new Promise<OffscreenChunkCanvasContext>((resolve) => {
			const image = new Image();

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			image.src = hoverChunk.url!;
			image.onload = (): void => {
				const canvas = new OffscreenCanvas(image.naturalWidth, image.naturalHeight);
				const context = canvas.getContext('2d', {
					willReadFrequently: true,
					alpha: false,
				});
				if (context) context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);

				const chunk: HoverChunkImageProperties = {
					properties: hoverChunk,
					x: (hoverChunk.range.depthFrom - lane.depthFrom) * scale,
					width: image.naturalWidth,
					scaleFactor: hoverChunk.originalResPxPerM ? scale / hoverChunk.originalResPxPerM : 0,
				};
				resolve({ context, chunk });
			};
		});

		promise.then((canvas) => {
			if (shouldUpdate) setOffscreenCanvas(canvas);
		});

		return (): void => {
			shouldUpdate = false;
		};
	}, [hoverChunk, lane.depthFrom, scale, shouldCalculate]);

	useEffect(() => {
		if (!coordinates || !offscreenCanvas || !offscreenCanvas.context || !positionY || !hoverChunk) {
			setHexColour(undefined);
			return;
		}

		const chunk = offscreenCanvas.chunk;
		const [, y] = coordinates;
		// Note: chunks are vertical, so coordinates must be transposed.
		// For overview chunks, `chunk.url` points to the full source image and the column we care
		// about lives at sourceCrop.{x,y}; offset both axes accordingly. Regular chunks have no
		// sourceCrop and the offsets fall through to 0 / chunk.width, matching prior behaviour.
		const cropX = chunk.properties.sourceCrop?.x ?? 0;
		const cropY = chunk.properties.sourceCrop?.y ?? 0;
		const cropWidth = chunk.properties.sourceCrop?.width ?? chunk.width;
		const imgX = cropX + (cropWidth - y / chunk.scaleFactor);
		const imgY = cropY + Math.round((positionY - hoverChunk?.range.depthFrom) * chunk.properties.originalResPxPerM);
		const pixData = offscreenCanvas.context.getImageData(imgX, imgY, 1, 1).data;
		const hexColour = rgbToHex([pixData[0], pixData[1], pixData[2]]);
		setHexColour(hexColour);
	}, [coordinates, positionY, hoverChunk, offscreenCanvas]);

	return useMemo(
		() => (positionY ? { positionY, colour: positionY ? hexColour : undefined } : undefined),
		[positionY, hexColour],
	);
};
